// Aftergrow offer (spec 9, prompt 04a): one plan for the whole page. Every offer's radios, the
// sticky bar's plan menu, the plan buttons and every [data-ag-for] element follow it. With a
// product, the offer, the sticky bar and the plan buttons add the plan to the cart through Horizon's
// cart event, so the cart drawer opens and redraws itself. Monthly Refill cannot be added until
// Slice 4b (until then it is not rendered). On the product page ?plan= preselects a plan and a
// refresh keeps it. While adding, the button says "Adding...".
import { CartLinesUpdateEvent } from '@shopify/events';

const PLANS = ['protocol', 'refill', 'single'];
const all = (selector) => [...document.querySelectorAll(selector)];
const offer = () => document.querySelector('[data-ag-offer][data-variant]');
const label = (p) => document.querySelector(`[data-ag-offer] input[data-ag-plan="${p}"]`)?.dataset.label || '';
const blocked = (p) => p === 'refill' && !offer()?.hasAttribute('data-refill-on');
let plan = 'protocol';

const render = () => {
  all('input[data-ag-plan]').forEach((radio) => (radio.checked = radio.dataset.agPlan === plan));
  all('select[data-ag-plan-select]').forEach((select) => (select.value = plan));
  all('[data-ag-for]').forEach((el) => (el.hidden = !el.dataset.agFor.split(' ').includes(plan)));
  const status = document.querySelector('[data-ag-refill-status]');
  all('[data-ag-offer-button], [data-ag-plan-button="current"]').forEach((button) => {
    button.textContent = label(plan) || button.textContent;
    // A button without a product stays disabled ("Checkout opens at launch").
    if (!offer() || (button.closest('[data-ag-offer]') && !button.closest('[data-ag-offer][data-variant]'))) return;
    if (blocked(plan)) {
      button.setAttribute('aria-disabled', 'true');
      if (status) button.setAttribute('aria-describedby', status.id);
    } else {
      button.removeAttribute('aria-disabled');
      button.removeAttribute('aria-describedby');
    }
  });
};

// Only a plan the page shows can be chosen (Monthly Refill is hidden until Slice 4b).
const shown = (p) => PLANS.includes(p) && Boolean(document.querySelector(`input[data-ag-plan="${p}"]`));

const setPlan = (next, user) => {
  if (!shown(next)) return;
  plan = next;
  render();
  if (user && document.querySelector('[data-ag-offer][data-url-plan]')) {
    const url = new URL(window.location.href);
    url.searchParams.set('plan', next);
    window.history.replaceState(window.history.state, '', url);
  }
};

const start = () => {
  const asked = new URLSearchParams(window.location.search).get('plan');
  const checked = document.querySelector('[data-ag-offer] input[data-ag-plan]:checked');
  const fromUrl = document.querySelector('[data-ag-offer][data-url-plan]') && shown(asked);
  setPlan(fromUrl ? asked : checked?.dataset.agPlan || 'protocol');
};

const add = async (button) => {
  const source = offer();
  if (!source || blocked(plan) || button.getAttribute('aria-busy') === 'true') return;
  const quantity = plan === 'protocol' ? Number(source.dataset.qtyProtocol) || 3 : 1;
  const line = plan === 'protocol' ? source.dataset.planProtocol : source.dataset.planSingle;
  const sections = all('cart-items-component[data-section-id]').map((el) => el.dataset.sectionId);
  const error = (button.closest('form') || source).querySelector('[data-ag-offer-error]');
  const routes = window.Theme?.routes || {};
  const deferred = CartLinesUpdateEvent.createPromise();
  const label = button.textContent;
  button.setAttribute('aria-busy', 'true');
  if (source.dataset.adding) button.textContent = source.dataset.adding;
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: source.dataset.variant, quantity }],
      promise: deferred.promise,
    })
  );
  try {
    const response = await fetch(`${routes.cart_add_url || '/cart/add'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(source.dataset.variant), quantity, properties: line ? { Plan: line } : {} }],
        sections: sections.join(','),
      }),
    });
    const data = await response.json();
    const cart = await (await fetch(`${routes.cart_url || '/cart'}.js`)).json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, sections: data.sections, didError: !response.ok, source: 'aftergrow-offer', itemCount: quantity },
    });
    if (error) {
      error.hidden = response.ok;
      error.textContent = response.ok ? '' : data.description || data.message || '';
    }
  } catch (problem) {
    deferred.reject(problem);
    if (error) {
      error.hidden = false;
      error.textContent = problem.message;
    }
  } finally {
    button.removeAttribute('aria-busy');
    button.textContent = label;
    render();
  }
};

document.addEventListener('change', (event) => {
  const target = event.target;
  if (target.matches?.('input[data-ag-plan]')) setPlan(target.dataset.agPlan, true);
  else if (target.matches?.('select[data-ag-plan-select]')) setPlan(target.value, true);
});

document.addEventListener('submit', (event) => {
  const form = event.target.closest?.('.ag-offer__form');
  if (!form || !offer()) return;
  event.preventDefault();
  const button = form.querySelector('[data-ag-offer-button]');
  if (button && button.getAttribute('aria-disabled') !== 'true') add(button);
});

document.addEventListener('click', (event) => {
  const button = event.target.closest?.('[data-ag-offer-button], [data-ag-plan-button]');
  if (!button) return;
  if (button.getAttribute('aria-disabled') === 'true') {
    event.preventDefault();
    return;
  }
  if (!button.matches('[data-ag-plan-button]')) return;
  event.preventDefault();
  if (button.dataset.agPlanButton !== 'current') setPlan(button.dataset.agPlanButton, true);
  add(button);
});

// Theme editor: selecting a plan's card version (or a block in it) checks that plan.
document.addEventListener('shopify:block:select', (event) => {
  const card = event.target.closest?.('[data-ag-card-plan]');
  if (card) setPlan(card.dataset.agCardPlan);
});
document.addEventListener('shopify:section:load', render);
window.addEventListener('pageshow', render);
start();
